import { useEffect } from 'react'

interface Heartbeat {
  timestamp: string
  active: boolean
}

interface ActivityLogEntry {
  timestamp: string
  message: string
}

interface WorkerPersona {
  graphic: string
  description: string
}

interface WorkerData {
  name: string
  role: string
  status: string
  currentTask?: string
  persona: WorkerPersona
  heartbeats: Heartbeat[]
  activityLog: ActivityLogEntry[]
}

interface WorkerModalProps {
  worker: WorkerData
  onClose: () => void
  isOpen: boolean
  isLoading?: boolean
}

export function WorkerModal({ worker, onClose, isOpen, isLoading = false }: WorkerModalProps) {
  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase()
    if (statusLower === 'active') return 'text-green-400'
    if (statusLower === 'inactive') return 'text-gray-400'
    return 'text-gray-400'
  }

  const getRoleColor = (role: string) => {
    const roleLower = role.toLowerCase()
    if (roleLower === 'demiurge') return 'text-cosmic-ether'
    if (roleLower === 'visionary') return 'text-cosmic-nebula'
    if (roleLower === 'voyager') return 'text-cosmic-star'
    if (roleLower === 'gatekeeper') return 'text-cosmic-plasma'
    return 'text-gray-400'
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="themed-panel border border-cosmic-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-cosmic-border bg-cosmic-depth">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Persona Graphic */}
              <div className="text-6xl">{worker.persona.graphic}</div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{worker.name}</h2>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`font-semibold capitalize ${getRoleColor(worker.role)}`}>
                    {worker.role}
                  </span>
                  <span className="text-gray-500">•</span>
                  <span className={`font-semibold ${getStatusColor(worker.status)}`}>
                    {worker.status}
                  </span>
                  {worker.currentTask && (
                    <>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400 font-mono">{worker.currentTask}</span>
                    </>
                  )}
                </div>
                <p className="text-gray-400 text-sm mt-1">{worker.persona.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Heartbeat Visualization */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="text-red-400">💓</span>
              Heartbeat History
            </h3>
            <div className="bg-cosmic-void border border-cosmic-border rounded-lg p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-24 text-gray-400">
                  Loading heartbeat data...
                </div>
              ) : worker.heartbeats.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-gray-500">
                  No heartbeat data available
                </div>
              ) : (
                (() => {
                  const beats = worker.heartbeats.slice(-30)
                  const width = 100
                  const height = 60
                  const padding = { top: 5, bottom: 20, left: 0, right: 0 }
                  const graphWidth = width - padding.left - padding.right
                  const graphHeight = height - padding.top - padding.bottom

                  // Build the line path with EKG-style spikes
                  const points = beats.map((beat, i) => {
                    const x = padding.left + (i / Math.max(beats.length - 1, 1)) * graphWidth
                    const y = beat.active
                      ? padding.top + graphHeight * 0.2  // High for active
                      : padding.top + graphHeight * 0.8  // Low for inactive
                    return { x, y, active: beat.active }
                  })

                  // Create path segments colored by status
                  const segments: { path: string; active: boolean }[] = []
                  for (let i = 0; i < points.length - 1; i++) {
                    const p1 = points[i]
                    const p2 = points[i + 1]
                    if (!p1 || !p2) continue
                    // Add small spike for heartbeat effect
                    const midX = (p1.x + p2.x) / 2
                    segments.push({
                      path: `M ${p1.x} ${p1.y} L ${midX} ${p1.y} L ${p2.x} ${p2.y}`,
                      active: p1.active
                    })
                  }

                  // Get time labels (first, middle, last)
                  const formatTime = (ts: string) => {
                    const d = new Date(ts)
                    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                  }
                  const firstBeat = beats[0]
                  const lastBeat = beats[beats.length - 1]
                  const firstTime = firstBeat ? formatTime(firstBeat.timestamp) : ''
                  const lastTime = lastBeat ? formatTime(lastBeat.timestamp) : ''

                  return (
                    <>
                      <svg
                        viewBox={`0 0 ${width} ${height}`}
                        className="w-full h-24"
                        preserveAspectRatio="none"
                      >
                        {/* Grid lines */}
                        <line x1={padding.left} y1={padding.top + graphHeight * 0.2} x2={width - padding.right} y2={padding.top + graphHeight * 0.2} stroke="#374151" strokeWidth="0.3" strokeDasharray="2,2" />
                        <line x1={padding.left} y1={padding.top + graphHeight * 0.8} x2={width - padding.right} y2={padding.top + graphHeight * 0.8} stroke="#374151" strokeWidth="0.3" strokeDasharray="2,2" />

                        {/* Line segments */}
                        {segments.map((seg, i) => (
                          <path
                            key={i}
                            d={seg.path}
                            fill="none"
                            stroke={seg.active ? '#4ade80' : '#f87171'}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        ))}

                        {/* Data points */}
                        {points.map((p, i) => {
                          const beat = beats[i]
                          if (!beat) return null
                          return (
                            <circle
                              key={i}
                              cx={p.x}
                              cy={p.y}
                              r="1"
                              fill={p.active ? '#4ade80' : '#f87171'}
                            >
                              <title>{formatTimestamp(beat.timestamp)} - {p.active ? 'Active' : 'Inactive'}</title>
                            </circle>
                          )
                        })}
                      </svg>
                      <div className="flex justify-between items-center text-xs text-gray-500 -mt-1">
                        <span>{firstTime}</span>
                        <div className="flex gap-4">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-400"></span>
                            Active
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-400"></span>
                            Inactive
                          </span>
                        </div>
                        <span>{lastTime}</span>
                      </div>
                    </>
                  )
                })()
              )}
            </div>
          </section>

          {/* Activity Log */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="text-cosmic-star">📝</span>
              Activity Log
            </h3>
            {isLoading ? (
              <div className="text-gray-400 text-center py-4">Loading activity log...</div>
            ) : worker.activityLog.length === 0 ? (
              <div className="text-gray-500 text-center py-4">No activity recorded</div>
            ) : (
              <div className="space-y-2">
                {worker.activityLog.slice().reverse().map((entry, index) => (
                  <div
                    key={index}
                    className="bg-cosmic-void border border-cosmic-border rounded-lg p-3 hover:border-cosmic-nebula/50 transition-colors"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <p className="text-gray-300 text-sm flex-1">{entry.message}</p>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
